import { Theme, Typography, TypographyProps } from "@mui/material";
import { SxProps } from "@mui/system";
import React, { FC, useEffect, useState } from "react";
import {
  font200,
  font300,
  font400,
  font500,
  font700,
  font900,
} from "../../styles/fontFamily";
import useSize from "../../hooks/useSize";

export interface IText {
  type?: "extraLight" | "light" | "medium" | "regular" | "bold" | "black";
  size?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "caption"
    | "button"
    | "overline";
  color?: TypographyProps["color"];
  style?: SxProps<Theme>;
  children: any;
  anyProps?: TypographyProps;
  align?: TypographyProps["align"];
  noWrap?: boolean;
}

const Text: FC<IText> = ({
  type = "regular",
  children,
  style,
  size,
  color,
  anyProps,
  align,
  noWrap,
}) => {
  const [fontType, setFontType] = useState<any>("");
  const [fontSize, setFontSize] = useState<any>(0);
  const [fontWeight, setFontWeight] = useState<any>(0);
  const [lineHeight, setLineHeight] = useState<any>("1rem");
  const { pxToRem } = useSize();

  useEffect(() => {
    setLineHeight(() => {
      switch (size) {
        case "overline":
          return "1rem";
        case "caption":
          return "1rem";
        case "subtitle2":
          return "1.25rem";
        case "subtitle1":
          return "1.5rem";
        case "body2":
          return "1.75rem";
        case "body1":
          return "1.75rem";
        case "h6":
          return "1.75rem";
        case "h5":
          return "2rem";
        case "h4":
          return "2.25rem";
        case "h3":
          return 1;
        case "h2":
          return 1;
        case "h1":
          return 1;
        default:
          return "1.25rem";
      }
    });
    return () => {};
  }, [size]);

  useEffect(() => {
    setFontSize(() => {
      switch (size) {
        case "overline":
          return pxToRem(10);
        case "caption":
          return pxToRem(12);
        case "subtitle2":
          return pxToRem(14);
        case "subtitle1":
          return pxToRem(16);
        case "body2":
          return pxToRem(18);
        case "body1":
          return pxToRem(20);
        case "h6":
          return pxToRem(20);
        case "h5":
          return pxToRem(24);
        case "h4":
          return pxToRem(32);
        case "h3":
          return pxToRem(48);
        case "h2":
          return pxToRem(60);
        case "h1":
          return pxToRem(96);
        default:
          return pxToRem(14);
      }
    });
    return () => {};
  }, [size]);

  useEffect(() => {
    setFontType(() => {
      switch (type) {
        case "extraLight":
          return font200;
        case "light":
          return font300;
        case "medium":
          return font500;
        case "regular":
          return font400;
        case "bold":
          return font700;
        case "black":
          return font900;
        default:
          return font400;
      }
    });

    return () => {};
  }, [type]);

  useEffect(() => {
    setFontWeight(() => {
      switch (type) {
        case "extraLight":
          return 200;
        case "light":
          return 300;
        case "regular":
          return 400;
        case "medium":
          return 500;
        case "bold":
          return 700;
        case "black":
          return 900;
        default:
          return 400;
      }
    });

    return () => {};
  }, [type]);

  return (
    <Typography
      {...anyProps}
      fontFamily={fontType}
      fontSize={fontSize}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      sx={{
        ...style,
      }}
      color={color ? color : "text.primary"}
      align={align}
      noWrap={noWrap}
    >
      {children}
    </Typography>
  );
};

export default Text;
